import { useContext, useEffect, useRef, useState } from 'react'
import NotificationContext from '@/context/NotificationContext'
import { FeatureFlag } from '@/optimizely/types'
import { useFeatureFlags } from '@/optimizely/useFeatureFlags'
import { tokenManager } from '@/services/TokenManager/TokenManager'
import { readAssistantUserId } from './history'
import { fetchAssistantNudge, type NudgeSourceOptions } from './nudgeTask'

/**
 * The proactive nudge: one urgent My Tasks item, offered under the top-bar
 * button ("Respond to invitation on "Dog bite" — due in 5 hs.").
 */
export interface AssistantNudge {
  /** Stable per task and action, so "Not Now" hides this one and not the next. */
  id: string
  /** One sentence, plain text. */
  message: string
  /** What the briefing's "What should I do next?" asks, by case title. */
  question: string
  /** The task's case: sent with the question as context, never in its text. */
  caseId: string
  /** What Review opens: the task's facts, shown without asking the model. */
  briefing: AssistantBriefing
}

/** A follow-up chip on the briefing: sends `question` as a normal message. */
export interface AssistantBriefingFollowUp {
  label: string
  question: string
  /** The case the question is about, sent as context with it. */
  caseId?: string
}

/**
 * The briefing card Review opens: the nudge's task, exactly as My Tasks has
 * it. A plain object; shown on screen only, never added to the conversation.
 */
export interface AssistantBriefing {
  caseId: string
  /** The full case title ('' when the case has none). */
  title: string
  /** The server's `action_type`, for analytics and the follow-ups. */
  actionType: string
  /** The server's action label, sentence case: "Respond to invitation". */
  actionLabel: string
  /** The card's time, spelled out: "expires in 5 hours". */
  due: string | null
  /** Whether the card shows that time as urgent (red) or plain. */
  dueUrgent: boolean
  /** What else the card shows: the other firm, or practice area and state. */
  facts: string[]
  /** One or two follow-up questions. */
  followUps: AssistantBriefingFollowUp[]
}

/** Quiet time after the last live notification before refetching. */
export const NUDGE_REFRESH_DEBOUNCE_MS = 2_500
/** Never more than one request per this long, however busy notifications are. */
export const NUDGE_REFRESH_FLOOR_MS = 60_000
/**
 * How long a briefing is kept once its panel is closed, from when its time
 * ("expires in 5 hours") was read: well inside the hour that time is rounded
 * to. Each fetch of the task renews it.
 */
export const BRIEFING_HOLD_MS = 15 * 60_000

interface CacheEntry {
  promise: Promise<AssistantNudge | null>
  /** When the request went out. */
  fetchedAt: number
  /** Set once it settles, so a remount shows it straight away. */
  value?: AssistantNudge | null
}

// One request per page load per user (and flag combination), shared by
// every mount. Memory only: a reload starts over.
const cache = new Map<string, CacheEntry>()

const startFetch = (key: string, options: NudgeSourceOptions) => {
  const entry: CacheEntry = {
    fetchedAt: Date.now(),
    promise: fetchAssistantNudge(options).catch(() => null),
  }
  entry.promise.then(value => {
    entry.value = value
  })
  cache.set(key, entry)
  return entry.promise
}

/** Tests only: forget what this page load has fetched. */
export const resetAssistantNudgeCache = () => cache.clear()

/**
 * The top urgent My Tasks item, from the same needs-action endpoint My
 * Tasks uses at its smallest limits. Fetched once per page load; refetched
 * after a live notification arrives or `refreshOn` (the route) changes, so a
 * task just finished stops being offered (debounced, at most once a minute).
 * No polling. Fetches nothing while `enabled` is false.
 */
export const useAssistantNudge = ({
  enabled,
  refreshOn = '',
}: {
  enabled: boolean
  /** A change here (e.g. the pathname) refetches, like a notification. */
  refreshOn?: string
}): AssistantNudge | null => {
  const {
    [FeatureFlag.MY_CASES_V4_5]: myCasesV45Enabled,
    [FeatureFlag.WITHHOLD_CLIENT_CONTACT_ENABLED]: withholdClientContactFlag,
  } = useFeatureFlags()
  const isV45 = !!myCasesV45Enabled
  const withholdClientContactEnabled = !!withholdClientContactFlag
  const userId = enabled
    ? readAssistantUserId(tokenManager.getAccessToken())
    : null
  const key = userId
    ? `${userId}:${isV45}:${withholdClientContactEnabled}`
    : null

  // Read directly: useNotifications() throws outside its provider.
  const notifications = useContext(NotificationContext)
  const newestNotificationId = notifications?.state.hasInitiallyLoaded
    ? notifications.state.notifications[0]?.id ?? ''
    : undefined

  const [result, setResult] = useState<{
    key: string
    nudge: AssistantNudge | null
  } | null>(null)

  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!key) return
    const options = {
      isV45,
      withholdClientContactEnabled,
      userId: userId ?? '',
    }
    const pending = cache.get(key)?.promise ?? startFetch(key, options)
    pending.then(nudge => {
      if (isMounted.current) setResult({ key, nudge })
    })
  }, [key, isV45, withholdClientContactEnabled, userId])

  // What has been seen so far; the first value of each is the baseline.
  const seen = useRef<{
    key: string
    notificationId?: string
    route: string
  } | null>(null)
  // A ref, not effect cleanup: a baseline-only rerun must not cancel a
  // refetch that is already waiting.
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(refreshTimer.current), [])
  // The key a waiting refetch may still run for.
  const activeKey = useRef<string | null>(null)

  useEffect(() => {
    activeKey.current = key
    const last = seen.current
    if (!key) {
      // Signed out or disabled: drop any refetch still waiting.
      clearTimeout(refreshTimer.current)
      seen.current = null
      return
    }
    if (last?.key !== key) {
      // A new user or new flags: the old key's refetch must not run.
      clearTimeout(refreshTimer.current)
      seen.current = {
        key,
        notificationId: newestNotificationId,
        route: refreshOn,
      }
      return
    }
    let changed = false
    if (newestNotificationId !== undefined) {
      if (last.notificationId === undefined) {
        last.notificationId = newestNotificationId
      } else if (last.notificationId !== newestNotificationId) {
        last.notificationId = newestNotificationId
        changed = true
      }
    }
    if (last.route !== refreshOn) {
      last.route = refreshOn
      changed = true
    }
    if (!changed) return

    const lastFetch = cache.get(key)?.fetchedAt ?? 0
    const wait = Math.max(
      NUDGE_REFRESH_DEBOUNCE_MS,
      lastFetch + NUDGE_REFRESH_FLOOR_MS - Date.now()
    )
    // A newer change cancels the wait and starts it again; a request
    // already out still lands.
    clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(() => {
      if (activeKey.current !== key) return
      if (readAssistantUserId(tokenManager.getAccessToken()) !== userId) return
      startFetch(key, {
        isV45,
        withholdClientContactEnabled,
        userId: userId ?? '',
      }).then(nudge => {
        if (isMounted.current) setResult({ key, nudge })
      })
    }, wait)
  }, [
    key,
    newestNotificationId,
    refreshOn,
    isV45,
    withholdClientContactEnabled,
    userId,
  ])

  if (!key) return null
  if (result?.key === key) return result.nudge
  return cache.get(key)?.value ?? null
}
